import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API_URL, fetchWithAuth, getImageUrl } from '../api';
import { format, parseISO } from 'date-fns';
import { Calendar, Clock, AlertCircle, Search } from 'react-feather';
import { FaTicketAlt } from 'react-icons/fa';

// --- COMPONENTE SKELETON (LA TARJETA FALSA) ---
const EventSkeleton = () => (
  <div className="relative bg-[#1a1a1a] border border-white/5 rounded-3xl overflow-hidden aspect-[4/5] animate-pulse">
    {/* Badge falsa */}
    <div className="absolute top-4 left-4 w-24 h-6 bg-white/10 rounded-full" />
    
    {/* Fondo gris suave */}
    <div className="w-full h-full bg-white/5" />

    {/* Contenido falso abajo */}
    <div className="absolute bottom-0 left-0 right-0 p-6 pt-16 bg-gradient-to-t from-black via-black/90 to-transparent">
        <div className="h-8 bg-white/10 rounded-lg w-3/4 mb-3" /> {/* Título */}
        <div className="h-4 bg-white/5 rounded w-1/2 mb-5" />    {/* Fecha */}
        <div className="h-12 bg-white/10 rounded-xl w-full" />   {/* Botón */}
    </div>
  </div>
);

export default function MyPasses() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeEvents, setActiveEvents] = useState([]);
  const [pastEvents, setPastEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // --- LOGIC: FETCHING ---
  useEffect(() => {
    const fetchUserEvents = async () => {
      try {
        // Simulamos un pequeño delay extra si quieres ver el efecto skeleton (opcional, quitar en prod)
        // await new Promise(resolve => setTimeout(resolve, 1000)); 

        const eventsData = await fetchWithAuth(`${API_URL}/my-qr-requests`);

        const rawActiveEvents = await Promise.all(
          eventsData.active_events.map(async (event) => {
            try {
              const qrData = await fetchWithAuth(`${API_URL}/events/${event.event_id}/qr`);
              return { 
                ...event, 
                qrStatus: qrData.status, 
                qrUrl: qrData.qr_url,
                scanned_time: qrData.scanned_time 
              };
            } catch (err) {
              return { ...event, qrStatus: 'error', qrUrl: null, scanned_time: null };
            }
          })
        );

        const rawPastEvents = await Promise.all(
          eventsData.past_events.map(async (event) => {
            try {
              const qrData = await fetchWithAuth(`${API_URL}/events/${event.event_id}/qr`);
              return { 
                  ...event, 
                  qrStatus: qrData.status, 
                  qrUrl: qrData.qr_url,
                  scanned_time: qrData.scanned_time 
              };
            } catch (err) {
              return { ...event, qrStatus: 'error', qrUrl: null };
            }
          })
        );

        const trulyActive = rawActiveEvents.filter(e => !e.scanned_time);
        const usedEvents = rawActiveEvents.filter(e => e.scanned_time);
        const finalPastEvents = [...rawPastEvents, ...usedEvents];

        setActiveEvents(trulyActive);
        setPastEvents(finalPastEvents);
      } catch (err) {
        setError(typeof err.message === 'string' ? err.message : 'Error al cargar entradas');
      } finally {
        setLoading(false);
      }
    };

    if (user) {
        fetchUserEvents();
    }
  }, [user, navigate]);

  // Si hay error, mostramos pantalla de error (esto sí bloquea, es lo único)
  if (error) return (
    <div className="min-h-screen pt-32 px-4 container mx-auto">
        <div className="p-6 bg-red-900/20 border border-red-500/20 rounded-2xl text-center max-w-lg mx-auto">
            <AlertCircle className="mx-auto text-red-400 mb-2" />
            <p className="text-red-300">{error}</p>
            <button onClick={() => window.location.reload()} className="mt-4 text-sm underline text-red-200">Intentar de nuevo</button>
        </div>
    </div>
  );

  const hasEvents = activeEvents.length > 0 || pastEvents.length > 0;

  return (
    <div className="min-h-screen w-full pt-24 pb-20 px-4 md:px-8 bg-black">
      
      <div className="max-w-6xl mx-auto space-y-10 animate-fade-in">
        
        {/* HEADER: CARGA SIEMPRE (INSTANTÁNEO) */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 border-b border-white/10 pb-6">
            <div>
                <h1 className="text-3xl md:text-4xl font-bold text-white mb-2 flex items-center gap-3">
                    <FaTicketAlt className="text-yellow-400" size={32}/> Mis Entradas
                </h1>
                <p className="text-gray-400">Gestiona tus accesos QR y revisa tu historial.</p>
            </div>
            
            {/* Resumen rápido (Solo aparece cuando termina de cargar y si hay eventos) */}
            {!loading && activeEvents.length > 0 && (
                 <div className="px-4 py-2 bg-gradient-to-r from-yellow-600/20 to-orange-600/20 border border-yellow-500/30 rounded-xl flex items-center gap-3 animate-fade-in">
                    <div className="text-right">
                        <p className="text-xs text-yellow-200 uppercase font-bold tracking-wider">Próximo Evento</p>
                        <p className="text-white font-bold text-sm truncate max-w-[150px]">{activeEvents[0].event.name}</p>
                    </div>
                 </div>
            )}
        </div>

        {/* 1. SECCIÓN ACTIVAS (Muestra Skeletons o Eventos Reales) */}
        <section>
            <div className="flex items-center gap-3 mb-6">
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                    <FaTicketAlt className="text-white/80" size={20}/> Activas
                </h2>
                {/* Badge de contador solo si ya cargó */}
                {!loading && (
                    <span className="bg-green-500 text-black px-2 py-0.5 rounded text-xs font-extrabold animate-scale-in">
                        {activeEvents.length}
                    </span>
                )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* LÓGICA DE CARGA MODERNA */}
                {loading ? (
                    // Muestra 3 tarjetas falsas mientras carga
                    <>
                        <EventSkeleton />
                        <EventSkeleton />
                        <EventSkeleton />
                    </>
                ) : (
                    // Muestra los eventos reales cuando termina
                    activeEvents.map((event) => (
                        <div 
                            key={event.event_id}
                            onClick={() => {
                            if(event.qrStatus === 'approved') navigate(`/qr/${event.event_id}`);
                            }}
                            className={`
                                relative group bg-[#1a1a1a] border border-white/10 rounded-3xl overflow-hidden shadow-2xl
                                hover:border-white/30 hover:shadow-purple-500/10 hover:-translate-y-1 transition-all duration-300
                                ${event.qrStatus === 'approved' ? 'cursor-pointer' : 'cursor-default'}
                            `}
                        >
                            {/* Poster Image */}
                            <div className="relative aspect-[4/5] overflow-hidden">
                                <img 
                                src={getImageUrl(event.event.image_url)} 
                                alt={event.event.name}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 opacity-90 group-hover:opacity-100"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-80"/>
                                
                                {/* Status Badge */}
                                <div className="absolute top-4 left-4">
                                    {event.qrStatus === 'approved' && (
                                        <span className="px-3 py-1.5 rounded-full bg-white text-black text-xs font-bold flex items-center gap-2 shadow-lg backdrop-blur-md">
                                            <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"/> LISTO
                                        </span>
                                    )}
                                    {event.qrStatus === 'pending' && (
                                        <span className="px-3 py-1.5 rounded-full bg-yellow-400 text-black text-xs font-bold shadow-lg">
                                            PENDIENTE
                                        </span>
                                    )}
                                    {event.qrStatus === 'error' && (
                                        <span className="px-3 py-1.5 rounded-full bg-red-500 text-white text-xs font-bold shadow-lg">
                                            ERROR
                                        </span>
                                    )}
                                </div>
                            </div>

                            {/* Content info overlay */}
                            <div className="absolute bottom-0 left-0 right-0 p-6 pt-16 bg-gradient-to-t from-black via-black/95 to-transparent">
                                <h3 className="text-2xl font-bold text-white mb-2 leading-tight drop-shadow-md">{event.event.name}</h3>
                                <p className="text-gray-300 mb-5 flex items-center gap-2 text-sm font-medium">
                                    <Calendar size={16} className="text-purple-400"/>
                                    {event.event.date ? format(parseISO(event.event.date), 'EEEE, d MMMM') : 'Fecha pendiente'}
                                </p>

                                {/* Action Button */}
                                {event.qrStatus === 'approved' ? (
                                    <button className="w-full py-3.5 bg-white text-black rounded-xl font-bold text-sm tracking-wide hover:bg-gray-200 transition flex items-center justify-center gap-2 shadow-lg group-hover:shadow-white/20">
                                        <FaTicketAlt size={18}/> ABRIR QR
                                    </button>
                                ) : (
                                    <div className="w-full py-3.5 bg-white/5 text-white/40 rounded-xl font-bold text-sm text-center border border-white/5 uppercase tracking-wide">
                                        {event.qrStatus === 'pending' ? 'Pendiente' : 'Soporte'}
                                    </div>
                                )}
                            </div>
                        </div>
                    ))
                )}
            </div>
        </section>

        {/* 2. HISTORIAL (Solo se muestra al terminar de cargar si hay datos) */}
        {!loading && pastEvents.length > 0 && (
            <section className="pt-8 border-t border-white/5 animate-fade-in">
                <div className="flex items-center gap-3 mb-6 opacity-60">
                    <h2 className="text-xl font-medium text-white flex items-center gap-2">
                        <Clock className="text-white/80"/> Historial de Eventos
                    </h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {pastEvents.map((event) => (
                        <div key={event.event_id} className="relative bg-[#ffffff02] border border-white/5 rounded-2xl overflow-hidden grayscale opacity-50 hover:opacity-100 hover:grayscale-0 transition-all duration-300 group">
                            <div className="flex h-24">
                                <div className="w-24 h-full shrink-0">
                                    <img 
                                        src={getImageUrl(event.event.image_url)} 
                                        className="w-full h-full object-cover"
                                        alt=""
                                    />
                                </div>
                                <div className="p-3 flex flex-col justify-center min-w-0">
                                    <h4 className="text-white text-sm font-bold mb-1 line-clamp-1">{event.event.name}</h4>
                                    <p className="text-[11px] text-gray-500 flex items-center gap-1 mb-2">
                                        <Calendar size={10}/> {event.event.date ? format(parseISO(event.event.date), 'd MMM yyyy') : ''}
                                    </p>
                                    <span className={`inline-block px-2 py-0.5 rounded text-[9px] font-bold w-fit uppercase tracking-wider ${event.scanned_time ? 'bg-green-900/30 text-green-400' : 'bg-gray-800 text-gray-400'}`}>
                                        {event.scanned_time ? 'Asististe' : 'Expirado'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </section>
        )}

        {/* 3. EMPTY STATE (Solo si terminó de cargar y NO hay nada) */}
        {!loading && !hasEvents && (
            <div className="py-24 text-center border border-dashed border-white/10 rounded-3xl bg-[#ffffff02] animate-fade-in">
                <div className="w-20 h-20 mx-auto mb-6 bg-white/5 rounded-full flex items-center justify-center text-white/20 animate-pulse">
                    <FaTicketAlt size={40}/>
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">Tu billetera está vacía</h3>
                <p className="text-gray-500 mb-8 max-w-md mx-auto">Aún no tienes entradas activas ni pasadas. Explora los eventos disponibles.</p>
                <button onClick={() => navigate('/explore')} className="px-8 py-4 rounded-full bg-white text-black font-bold hover:bg-gray-200 transition shadow-xl hover:shadow-white/10 flex items-center gap-2 mx-auto">
                    <Search size={18}/>
                    Explorar Eventos
                </button>
            </div>
        )}
      </div>
    </div>
  );
}